import { z } from "zod";
import { InsightScope, InsightVisibility } from "./insight";

// ---------------------------------------------------------------------------
// Actions ("att göra")
//
// Concrete to-do items proposed by the AI generators alongside insights and
// accepted (or skipped) by a team leader. Stored at:
//   Businesses/{businessId}/actions/{actionId}   (actionId === dedupeKey)
//
// Same idempotency pattern as insights: the doc id is the dedupeKey, so a
// nightly re-run upserts the same proposal in place. User decisions
// (accepted/done/skipped) always win over regeneration — see
// `upsertActionProposal` in @graftus/db.
// ---------------------------------------------------------------------------

/** Manual "Agera" item kinds from the sketch: praise, boost, talk, follow-up. */
export const ActionKind = z.enum(["berom", "boosta", "samtal", "uppfoljning"]);
export type ActionKind = z.infer<typeof ActionKind>;

/** Who created it: a person in Agera, or the platform's generators. */
export const ActionSource = z.enum(["manual", "platform"]);
export type ActionSource = z.infer<typeof ActionSource>;

/** Sketch priority Låg/Mellan/Hög mapped onto the 0..100 `priority` score. */
export const ACTION_PRIORITY_BY_LEVEL: Record<1 | 2 | 3, number> = { 1: 30, 2: 60, 3: 90 };

export const ActionStatus = z.enum([
  "proposed",
  "accepted",
  "done",
  "skipped",
  "expired",
]);
export type ActionStatus = z.infer<typeof ActionStatus>;

/**
 * Legal user-driven transitions. Proposals are accepted or skipped; accepted
 * actions are completed or skipped. Everything else is store-owned
 * (regeneration re-proposes, the expiry pass expires).
 */
export const ACTION_STATUS_TRANSITIONS: Partial<
  Record<ActionStatus, ActionStatus[]>
> = {
  proposed: ["accepted", "skipped"],
  accepted: ["done", "skipped"],
};

// ---------------------------------------------------------------------------
// Generator output
// ---------------------------------------------------------------------------

export const GeneratedAction = z.object({
  generatorKey: z.string().min(1),
  /** `${insightDedupeKey}:action:${slug(suffix)}` — the Firestore doc id. */
  dedupeKey: z.string().min(1),
  /** The parent insight's dedupeKey (its doc id), if any. */
  insightId: z.string().min(1).nullable().default(null),
  /** Snapshot of the parent insight's title so the todo list needs no join. */
  insightTitle: z.string().nullable().default(null),
  scope: InsightScope.default("business"),
  salesTeamId: z.string().min(1).nullable().default(null),
  memberId: z.string().min(1).nullable().default(null),
  visibility: InsightVisibility.default("business"),
  /** Required when visibility is "owner": the only uid allowed to see it. */
  ownerUid: z.string().min(1).nullable().default(null),

  title: z.string().min(1).max(80),
  description: z.string().min(1).max(300),
  /** Measurable expected effect, checked by the follow-up generator. */
  expectedEffect: z.string().max(200).nullable().default(null),
  /**
   * Active KPI definition the action targets, when it aims at a specific
   * number. Validated by the emitting generator against real definitions -
   * never trusted from the model. The follow-up generator uses it as the
   * primary yardstick for the effect evaluation.
   */
  expectedKpiDefinitionId: z.string().min(1).nullable().default(null),
  priority: z.number().min(0).max(100),

  // provenance + quality
  confidence: z.number().min(0).max(1).nullable().default(null),
  promptVersion: z.number().int().nonnegative().nullable().default(null),
  modelId: z.string().min(1).nullable().default(null),
  contextFingerprint: z.string().min(1).nullable().default(null),
});
export type GeneratedAction = z.infer<typeof GeneratedAction>;

// ---------------------------------------------------------------------------
// Firestore document shape
// ---------------------------------------------------------------------------

export interface ActionRecord {
  id: string;
  businessId: string;
  generatorKey: string;
  dedupeKey: string;
  insightId: string | null;
  insightTitle: string | null;
  scope: InsightScope;
  salesTeamId: string | null;
  memberId: string | null;
  visibility: InsightVisibility;
  ownerUid: string | null;

  title: string;
  description: string;
  expectedEffect: string | null;
  expectedKpiDefinitionId: string | null;
  priority: number;

  confidence: number | null;
  promptVersion: number | null;
  modelId: string | null;
  contextFingerprint: string | null;

  status: ActionStatus;
  acceptedBy: string | null;
  acceptedAt: string | null;
  completedBy: string | null;
  completedAt: string | null;
  skippedBy: string | null;
  skippedAt: string | null;
  /** Optional user note captured when the action is marked done. */
  outcomeNote: string | null;
  /** Stamped by the follow-up generator once the effect has been evaluated. */
  evaluatedAt: string | null;
  followUpInsightId: string | null;
  createdAt: string | null;
  updatedAt: string | null;

  /** Manual items (Agera). Absent on generator output = platform. */
  kind?: ActionKind | null;
  source?: ActionSource;
  /** 1 = Låg, 2 = Mellan, 3 = Hög. */
  priorityLevel?: 1 | 2 | 3 | null;
  /** Roster member id of the department head it was delegated to. */
  delegatedToMemberId?: string | null;
  createdBy?: string | null;
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const CreateManualActionInput = z.object({
  businessId: z.string().min(1),
  kind: ActionKind,
  memberId: z.string().min(1).nullable().optional(),
  salesTeamId: z.string().min(1).nullable().optional(),
  text: z.string().min(1).max(300),
  priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
  delegatedToMemberId: z.string().min(1).nullable().optional(),
  /**
   * A caller-supplied, stable id for a suggestion the action was added from
   * (e.g. a Bruttovinst improvement suggestion, GRA-209) - not a persisted
   * `insights` doc id. Doubles as the action's own doc id, so repeat clicks
   * on the same suggestion are idempotent instead of piling up duplicates.
   */
  insightId: z.string().min(1).nullable().optional(),
});
export type CreateManualActionInput = z.infer<typeof CreateManualActionInput>;

export const UpdateManualActionInput = z.object({
  businessId: z.string().min(1),
  actionId: z.string().min(1),
  text: z.string().min(1).max(300).optional(),
  priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
  delegatedToMemberId: z.string().min(1).nullable().optional(),
  /** true marks done, false reopens (accepted). */
  done: z.boolean().optional(),
});
export type UpdateManualActionInput = z.infer<typeof UpdateManualActionInput>;

export const ListActionsInput = z.object({
  businessId: z.string().min(1),
  statuses: z.array(ActionStatus).optional(),
  scope: InsightScope.optional(),
  /** When set, returns only actions about this team member. */
  memberId: z.string().optional(),
});
export type ListActionsInput = z.infer<typeof ListActionsInput>;

/** Shared identity for the single-action mutations (accept/skip/complete). */
const ActionRefInput = z.object({
  businessId: z.string().min(1),
  actionId: z.string().min(1),
});

export const AcceptActionInput = ActionRefInput;
export type AcceptActionInput = z.infer<typeof AcceptActionInput>;

export const SkipActionInput = ActionRefInput;
export type SkipActionInput = z.infer<typeof SkipActionInput>;

export const CompleteActionInput = ActionRefInput.extend({
  outcomeNote: z.string().max(280).optional(),
});
export type CompleteActionInput = z.infer<typeof CompleteActionInput>;

// ---------------------------------------------------------------------------
// Tuning constants
// ---------------------------------------------------------------------------

/** Max action proposals persisted per generator run (sliced by priority). */
export const ACTION_PROPOSALS_MAX_PER_RUN = 8;
/** Wait at least this long after completion before evaluating the effect. */
export const ACTION_EFFECT_MIN_DAYS = 10;
/** Stop evaluating an action's effect after this long. */
export const ACTION_EFFECT_MAX_DAYS = 60;
/** How long a follow-up insight card stays on the dashboard (expiresAt). */
export const ACTION_FOLLOWUP_TTL_DAYS = 14;
